import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { formatGP } from "@/lib/format";
import OrderView from "@/components/OrderView";

export default function Checkout() {
  const { items, total, clear, remove } = useCart();
  const { user } = useAuth();
  const { settings } = useOutletContext();
  const [f, setF] = useState({ email: user?.email || "", contact: "", rsn: "", notes: "", payment_method: "osrs-gp" });
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { ...f, email: f.email || user?.email, items: items.map((i) => ({ product_id: i.id, qty: 1 })) });
      sessionStorage.setItem(`bomber_autoconnect_${data.code}`, "1");
      setOrder(data);
      clear();
      toast.success(`Order ${data.code} locked in`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (order) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-16" data-testid="checkout-success">
        <div className="overline-label mb-3">// RSN reserved</div>
        <h1 className="font-display text-6xl chrome-text mb-8 leading-none">Name locked. Complete payment.</h1>
        <OrderView o={order} payment={settings?.payment_instructions} />
        <p className="text-sm text-[#999] mt-6">Save your code. Track it any time with your email on the <Link to="/track" className="text-[#ff2a55] underline">tracking page</Link>.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-5 md:px-10 pt-16" data-testid="checkout-page">
      <div className="overline-label mb-4">// final approach</div>
      <h1 className="font-display text-6xl md:text-8xl leading-[0.9] chrome-text mb-12">Checkout</h1>
      {items.length === 0 ? (
        <div className="py-20 text-[#888]" data-testid="checkout-empty">Your payload is empty. <Link to="/shop" className="text-[#ff2a55] underline">Pick a target</Link>.</div>
      ) : (
        <form onSubmit={submit} className="grid lg:grid-cols-[1.3fr_1fr] gap-10" data-testid="checkout-form">
          <div className="space-y-5">
            <div><label className="overline-label block mb-2">Email (used to securely track this order)</label><input required type="email" className="field" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="checkout-email-input" /></div>
            <div><label className="overline-label block mb-2">Discord username or ID (for auto ranks)</label><input className="field" placeholder="e.g. bomberfan or 123456789012345678" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} data-testid="checkout-contact-input" /></div>
            <div><label className="overline-label block mb-2">Payment method</label>
              <select className="field" value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })} data-testid="checkout-payment-select">
                <option value="osrs-gp">OSRS GP — manually confirmed</option>
              </select>
            </div>
            <div><label className="overline-label block mb-2">Notes (optional)</label><textarea className="field" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="checkout-notes-input" /></div>
            <p className="text-xs text-[#888]">{settings?.payment_instructions}</p>
          </div>
          <div className="border border-[#ff2a55]/40 bg-[#090909] p-6 h-fit space-y-4 lg:sticky lg:top-24">
            <div className="overline-label">Payload</div>
            {items.map((it) => (
              <div key={it.id} className="flex justify-between items-center border-b border-white/10 pb-3" data-testid={`checkout-item-${it.id}`}>
                <div><div className="font-display text-2xl chrome-text-sm">{it.title}</div><button type="button" onClick={() => remove(it.id)} className="text-[10px] tracking-[0.2em] uppercase text-[#777] hover:text-[#ff2a55] transition-colors">Remove</button></div>
                <div className="font-display text-xl hot-text">{formatGP(it.price)}</div>
              </div>
            ))}
            <div className="flex justify-between items-end"><span className="overline-label">Total</span><span className="font-display text-5xl hot-text" data-testid="checkout-total">{formatGP(total)} GP</span></div>
            <button className="btn-bomb w-full !text-xl" disabled={busy} data-testid="checkout-submit-button">{busy ? "Locking..." : "Place order"}</button>
          </div>
        </form>
      )}
    </div>
  );
}
